import { Injectable, WritableSignal, signal } from '@angular/core';
import { Fyis } from '@local/client-contracts';
import { Syncs, SyncsRpcInvoker } from '@local/common';
import { Logger } from '@unleash-tech/js-logger';
import { Observable, firstValueFrom, map } from 'rxjs';
import { LogService, ServicesRpcService } from './';

@Injectable({
  providedIn: 'root',
})
export class SyncsService implements Syncs {
  private logger: Logger;
  private service: Syncs;
  forceShowSyncs: WritableSignal<{ [linkId: string]: boolean }> = signal({});

  linksSyncStatuses: { [linkId: string]: Fyis.SyncStatus };

  constructor(
    private services: ServicesRpcService,
    log: LogService
  ) {
    this.logger = log.scope('SyncsService');
    this.service = this.services.invokeWith(SyncsRpcInvoker, 'syncs');
    this.initLinkStatus();

    this.latest$.subscribe(() => {
      this.forceShowSyncs.set({});
    });
  }

  get all$(): Observable<Fyis.Sync[]> {
    return this.service.all$;
  }

  get disableSync$(): Observable<{ [linkId: string]: boolean }> {
    return this.service.disableSync$;
  }

  get latest$(): Observable<Fyis.Sync[]> {
    return this.service.latest$;
  }

  refresh(linkIds?: string[]): Promise<void> {
    return this.service.refresh(linkIds);
  }

  updateAutomaticSync(linkId: string, disable: boolean): Promise<void> {
    return this.service.updateAutomaticSync(linkId, disable);
  }

  forceSync(linkId: string): Promise<void> {
    return this.service.forceSync(linkId);
  }

  async getLatestSync(linkIds: string[]): Promise<Fyis.Sync[]> {
    const syncs = [];
    const initial = await firstValueFrom(this.all$);
    const latest = await firstValueFrom(this.latest$);
    for (const linkId of linkIds || []) {
      let sync = latest?.find((s) => s.linkId === linkId);
      if (!sync) {
        sync = initial?.find((s) => s.linkId === linkId);
      }
      if (sync) {
        syncs.push(sync);
      }
    }
    return syncs;
  }

  private initLinkStatus() {
    this.all$
      .pipe(
        map<Fyis.Sync[], { [linkId: string]: Fyis.SyncStatus }>((syncs: Fyis.Sync[]) => {
          return syncs?.reduce((acc, curr) => {
            acc[curr.linkId] = curr.status;
            return acc;
          }, {});
        })
      )
      .subscribe((linksSyncStatuses) => {
        this.linksSyncStatuses = linksSyncStatuses;
      });
  }

  forceShowSync(linkId: string, show: boolean) {
    if (show) {
      this.forceShowSyncs.update((prev) => ({ ...prev, [linkId]: true }));
    } else {
      this.forceShowSyncs.update((prev) => {
        const newState = { ...prev };
        delete newState[linkId];
        return newState;
      });
    }
  }
}
